import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CashReceiptService } from "services/cash-receipt";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { numberToWords, getPrintTemplate } from "./components/printTemplate";

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) return "";
  if (!hasAllDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) throw new Error(`${label} có tháng không hợp lệ`);
  if (year < 1900 || year > 9999) throw new Error(`${label} có năm không hợp lệ`);
  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) throw new Error(`${label} có ngày không hợp lệ`);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

export default function CashReceiptDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    address: "",
    bookNumber: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    payerName: "",
    payerAddress: "",
    reason: "",
    amount: "",
    attachments: "",
    originalDocuments: "",
    exchangeRate: "",
    convertedAmount: "",
    note: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await CashReceiptService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          address: data.address || "",
          bookNumber: data.bookNumber || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          payerName: data.payerName || "",
          payerAddress: data.payerAddress || "",
          reason: data.reason || "",
          amount: formatVndIntegerInput(data.amount || ""),
          attachments: data.attachments || "",
          originalDocuments: data.originalDocuments || "",
          exchangeRate: formatVndIntegerInput(data.exchangeRate || ""),
          convertedAmount: formatVndIntegerInput(data.convertedAmount || ""),
          note: data.note || "",
        });

        setDocumentDateParts(splitDateParts(data.documentDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu phiếu thu");
      }
    })();
  }, [id]);

  const derivedAmount = useMemo(() => roundMoney(header.amount), [header.amount]);
  const derivedExchangeRate = useMemo(() => roundMoney(header.exchangeRate), [header.exchangeRate]);
  const derivedConverted = useMemo(() => roundMoney(header.convertedAmount), [header.convertedAmount]);
  const derivedAmountText = useMemo(
    () => (derivedAmount > 0 ? numberToWords(derivedAmount) : ""),
    [derivedAmount]
  );

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const address = String(header.address || "").trim();
    const documentDate = validateDateParts(documentDateParts, "Ngày lập phiếu");
    const bookNumber = String(header.bookNumber || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const debitAccount = String(header.debitAccount || "").trim();
    const creditAccount = String(header.creditAccount || "").trim();
    const payerName = String(header.payerName || "").trim();
    const payerAddress = String(header.payerAddress || "").trim();
    const reason = String(header.reason || "").trim();
    const amount = derivedAmount;
    const amountText = String(derivedAmountText || "").trim();
    const attachments = String(header.attachments || "").trim();
    const originalDocuments = String(header.originalDocuments || "").trim();
    const exchangeRate = derivedExchangeRate;
    const convertedAmount = derivedConverted;
    const note = String(header.note || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!address) throw new Error("Địa chỉ không được để trống");
    if (!documentDate) throw new Error("Ngày lập phiếu không được để trống");
    if (!documentNumber) throw new Error("Số phiếu không được để trống");
    if (!payerName) throw new Error("Họ tên người nộp tiền không được để trống");
    if (!payerAddress) throw new Error("Địa chỉ không được để trống");
    if (!reason) throw new Error("Lý do nộp không được để trống");
    if (amount <= 0) throw new Error("Số tiền phải lớn hơn 0");
    if (!amountText) throw new Error("Số tiền bằng chữ không được để trống");

    return {
      unit,
      address,
      documentDate,
      bookNumber,
      documentNumber,
      debitAccount,
      creditAccount,
      payerName,
      payerAddress,
      reason,
      amount,
      amountText,
      attachments,
      originalDocuments,
      exchangeRate,
      convertedAmount,
      note,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await CashReceiptService.create(payload);
        toast.success("Tạo phiếu thu thành công");
      } else {
        await CashReceiptService.update(id, payload);
        toast.success("Cập nhật phiếu thu thành công");
      }
      navigation("/templates/cash-receipt");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        amountText: derivedAmountText,
        documentDate: composeDate(documentDateParts),
        amount: derivedAmount,
        exchangeRate: derivedExchangeRate,
        convertedAmount: derivedConverted,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.address}
                        onChange={(event) => setHeaderField("address", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 01 - TT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 26, textTransform: "uppercase" }}>
                    PHIẾU THU
                  </div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />
                    {" "}tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />
                    {" "}năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField("year", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 8, lineHeight: 1.7 }}>
                  <div>
                    Quyển số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.bookNumber}
                      onChange={(event) => setHeaderField("bookNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Nợ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.debitAccount}
                      onChange={(event) => setHeaderField("debitAccount", event.target.value)}
                    />
                  </div>
                  <div>
                    Có:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.creditAccount}
                      onChange={(event) => setHeaderField("creditAccount", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 10, lineHeight: 1.8 }}>
                  <div>
                    Họ và tên người nộp tiền:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 360 }}
                      value={header.payerName}
                      onChange={(event) => setHeaderField("payerName", event.target.value)}
                    />
                  </div>
                  <div>
                    Địa chỉ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={header.payerAddress}
                      onChange={(event) => setHeaderField("payerAddress", event.target.value)}
                    />
                  </div>
                  <div>
                    Lý do nộp:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={header.reason}
                      onChange={(event) => setHeaderField("reason", event.target.value)}
                    />
                  </div>
                  <div>
                    Số tiền:{" "}
                    <input
                      disabled={containView}
                      inputMode="numeric"
                      style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                      value={header.amount}
                      onChange={(event) => setHeaderField("amount", formatVndIntegerInput(event.target.value))}
                    />
                    {" "}(Viết bằng chữ):{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={derivedAmountText}
                      readOnly
                    />
                  </div>
                  <div>
                    Kèm theo:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 220 }}
                      value={header.attachments}
                      onChange={(event) => setHeaderField("attachments", event.target.value)}
                    />
                    {" "}chứng từ gốc.
                  </div>
                </div>

                <div style={{ marginTop: 12, lineHeight: 1.8 }}>
                  <div>
                    Đã nhận đủ số tiền (viết bằng chữ):{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 520 }}
                      value={derivedAmountText}
                      readOnly
                    />
                  </div>
                  <div>
                    + Tỷ giá ngoại tệ (vàng bạc, đá quý):{" "}
                    <input
                      disabled={containView}
                      inputMode="numeric"
                      style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                      value={header.exchangeRate}
                      onChange={(event) => setHeaderField("exchangeRate", formatVndIntegerInput(event.target.value))}
                    />
                  </div>
                  <div>
                    + Số tiền quy đổi:{" "}
                    <input
                      disabled={containView}
                      inputMode="numeric"
                      style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                      value={header.convertedAmount}
                      onChange={(event) => setHeaderField("convertedAmount", formatVndIntegerInput(event.target.value))}
                    />
                  </div>
                  <div>
                    (Liên gửi ra ngoài phải đóng dấu)
                  </div>
                  <div>
                    * Ghi chú:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 260 }}
                      value={header.note}
                      onChange={(event) => setHeaderField("note", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
